import { FunctionComponent, useState, useRef, useEffect, FormEvent } from 'react'
import { Subscription } from 'rxjs'
import { delay } from 'rxjs/operators'
import { submitPasswordReset } from 'services/SessionService'
import Icon from 'components/shared/Icon'
import Modal from 'components/shared/Modal'
import notificationToast from 'helpers/notificationToast'
import CheckIcon from '@mui/icons-material/CheckCircle'
import { toast } from 'react-toastify'
import { Loader2 } from 'lucide-react'

interface PasswordResetOverlayProps {
  email: string
  openPasswordResetModal: boolean
  setOpenPasswordResetModal: (open: boolean) => void
}

const PasswordResetOverlay: FunctionComponent<PasswordResetOverlayProps> = ({
  email,
  openPasswordResetModal,
  setOpenPasswordResetModal
}) => {
  const oldPasswordRef = useRef<HTMLInputElement>(null)
  const newPasswordRef = useRef<HTMLInputElement>(null)
  const repeatPasswordRef = useRef<HTMLInputElement>(null)
  const subscriptionRef = useRef<Subscription | null>(null)

  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [passwordFeedback, setPasswordFeedback] = useState<{ warning?: string, suggestions?: string[] } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => () => { subscriptionRef.current?.unsubscribe() }, [])

  const resetFields = (): void => {
    setOldPassword('')
    setNewPassword('')
    setRepeatPassword('')
    setErrorMessage(null)
    setPasswordFeedback(null)
    setSubmitting(false)
  }

  const handleCancel = (): void => {
    subscriptionRef.current?.unsubscribe()
    setOpenPasswordResetModal(false)
    resetFields()
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()

    if (oldPassword === '') {
      oldPasswordRef.current?.focus()
      return setErrorMessage('Please enter your old password.')
    }

    if (newPassword.includes(' ')) {
      newPasswordRef.current?.focus()
      return setErrorMessage('Please try again. Your password cannot contain spaces.')
    }

    if (newPassword.length < 8) {
      newPasswordRef.current?.focus()
      return setErrorMessage('Please try again. Your password must be longer than 8 characters.')
    }

    if (newPassword !== repeatPassword) {
      repeatPasswordRef.current?.focus()
      setRepeatPassword('')
      return setErrorMessage('Please try again. Your password entries do not match.')
    }

    setSubmitting(true)
    setErrorMessage(null)
    setPasswordFeedback(null)

    subscriptionRef.current = submitPasswordReset({ oldPassword, newPassword, mail: email })
      .pipe(delay(1000))
      .subscribe(({ success, weakPassword, warning, suggestions }: { success: boolean, weakPassword?: boolean, warning?: string, suggestions?: string[] }) => {
        if (!success) {
          setSubmitting(false)
          if (weakPassword === true) {
            setPasswordFeedback({ warning, suggestions })
          } else {
            setErrorMessage('Please try again. Your old password does not match what we have in our system.')
            setOldPassword('')
            oldPasswordRef.current?.focus()
          }
        } else {
          notificationToast(<CheckIcon />, toast.TYPE.SUCCESS, 'Password changed successfully', 'bg-dark-sea-green')
          setOpenPasswordResetModal(false)
          resetFields()
        }
      })
  }

  return (
    <Modal
      open={openPasswordResetModal}
      onClose={handleCancel}
      title='Reset password'
      size='medium'
      actions={
        <>
          <button
            type='button'
            onClick={handleCancel}
            disabled={submitting}
            className='pointer bg-transparent bn f5 black ph5 pv4 br3 hover-bg-ghost-white'
          >
            Cancel
          </button>
          <button
            type='submit'
            form='resetPasswordForm'
            disabled={submitting}
            className='pointer bg-suez-canal br3 ph6 pv4 white f5 bn hover-o-90 flex items-center gap2'
          >
            {submitting && <Loader2 size={16} className='spin-to-win' />}
            Submit
          </button>
        </>
      }
    >
      <form id='resetPasswordForm' onSubmit={handleSubmit} className='flex flex-column gap4'>
        {errorMessage !== null
          ? (
            <div className='crimson f5'>
              <Icon name='icon-alert-circle' /> {errorMessage}
            </div>
            )
          : null}
        <div>
          <label className='db mb2 f6 grey'>Email</label>
          <input className='w-100 h3 f5 ba b--bright-grey br2 ph3' type='text' value={email} disabled />
        </div>
        <div>
          <label className='db mb2 f6 grey'>Old password</label>
          <input
            className='w-100 h3 f5 ba b--bright-grey br2 ph3'
            autoFocus
            type='password'
            ref={oldPasswordRef}
            value={oldPassword}
            onChange={e => { setOldPassword(e.target.value); setErrorMessage(null); setPasswordFeedback(null) }}
          />
        </div>
        <div>
          <label className='db mb2 f6 grey'>New password</label>
          <input
            className='w-100 h3 f5 ba b--bright-grey br2 ph3'
            type='password'
            ref={newPasswordRef}
            value={newPassword}
            onChange={e => { setNewPassword(e.target.value); setErrorMessage(null); setPasswordFeedback(null) }}
          />
        </div>
        <div>
          <label className='db mb2 f6 grey'>Repeat new password</label>
          <input
            className='w-100 h3 f5 ba b--bright-grey br2 ph3'
            type='password'
            ref={repeatPasswordRef}
            value={repeatPassword}
            onChange={e => { setRepeatPassword(e.target.value); setErrorMessage(null); setPasswordFeedback(null) }}
          />
        </div>
        {(passwordFeedback != null) && (
          <div className='crimson f6 ph5'>
            <p className='mv0'>
              <Icon name='icon-alert-circle' />{' '}
              {passwordFeedback.warning ?? 'Your password is not strong enough. Please try again.'}
            </p>
            {(passwordFeedback.suggestions != null) && passwordFeedback.suggestions.length > 0 && (
              <ul className='mt2 mb0 pl4'>
                {passwordFeedback.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </div>
        )}
      </form>
    </Modal>
  )
}

export default PasswordResetOverlay
